import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../components/Config/api.service';
import { notify } from '../../components/Utils/Notifications';
import { signalerErreur } from '../../components/Utils/signalerErreur';
import { FiEdit2, FiTrash2, FiCheck, FiX, FiPlus, FiLoader } from 'react-icons/fi';

function AssignmentManager({ assignments = [], setAssignments, setAssignmentOptions, onClose, showColumn = true, setShowColumn }) {
    const { t } = useTranslation();
    const [list, setList] = useState(assignments || []);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState('');

    useEffect(() => setList(assignments || []), [assignments]);

    const refresh = async () => {
        try {
            const res = await apiService.getElement('/employee/assignments');
            const assigns = res.data.assignments || [];
            setList(assigns);
            if (setAssignments) setAssignments(assigns);
            if (setAssignmentOptions) setAssignmentOptions(assigns.map(a => ({ value: a.id, label: a.name })));
        } catch (e) {
            signalerErreur(e, 'AssignmentManager.refresh');
            notify(t('assignmentManager.refreshError'), '', 3);
        }
    };

    useEffect(() => { refresh(); }, []);

    const handleCreate = async () => {
        const name = input.trim();
        if (!name) return;
        setLoading(true);
        try {
            const res = await apiService.saveElement('/employee/assignment', { name });
            const newAssign = res.data.result || res.data;
            await refresh();
            setInput('');
            notify(t('assignmentManager.created'), '', 2);
        } catch (e) {
            signalerErreur(e, 'AssignmentManager.handleCreate');
            notify(t('assignmentManager.createError'), '', 3);
        } finally {
            setLoading(false);
        }
    };

    const startEdit = (a) => {
        setEditingId(a.id);
        setEditingName(a.name || '');
    };

    const saveEdit = async (id) => {
        if (!editingName.trim()) return;
        setLoading(true);
        try {
            await apiService.updateElementById('/employee/assignment', id, { name: editingName });
            await refresh();
            setEditingId(null);
            notify(t('assignmentManager.updated'), '', 2);
        } catch (e) {
            signalerErreur(e, 'AssignmentManager.saveEdit');
            notify(t('assignmentManager.updateError'), '', 3);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('assignmentManager.deleteConfirm'))) return;
        setLoading(true);
        try {
            await apiService.updateElementById('/employee/delete-assignment', id, {});
            await refresh();
            notify(t('assignmentManager.deleted'), '', 2);
        } catch (e) {
            signalerErreur(e, 'AssignmentManager.handleDelete');
            notify(t('assignmentManager.deleteError'), '', 3);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">{t('assignmentManager.title')}</h3>
                <div className="flex items-center gap-2">
                    <label className="label cursor-pointer">
                        <span className="label-text mr-2 text-sm">{t('assignmentManager.showColumn')}</span>
                        <input
                            type="checkbox"
                            className="toggle toggle-sm checked:bg-[--color-orange]"
                            checked={showColumn}
                            onChange={e => setShowColumn?.(e.target.checked)}
                            aria-label={t('assignmentManager.showColumnAria')}
                        />
                    </label>
                    {/* <button className="btn btn-xs btn-outline" onClick={onClose}>Fermer</button> */}
                </div>
            </div>

            <div className="flex gap-2 mb-4">
                <input
                    className="input input-sm input-bordered flex-1"
                    placeholder={t('assignmentManager.addPlaceholder')}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                />
                <button className="btn btn-sm btn-primary" onClick={handleCreate} disabled={loading || !input.trim()}>
                    {loading ? <FiLoader className="animate-spin" /> : <FiPlus />} {t('assignmentManager.add')}
                </button>
            </div>

            <ul className="divide-y">
                {list.length === 0 && <li className="py-4 text-center text-gray-400">{t('assignmentManager.empty')}</li>}
                {list.map(a => (
                    <li key={a.id} className="flex items-center py-2">
                        {editingId === a.id ? (
                            <>
                                <input className="input input-sm input-bordered flex-1 mr-2" value={editingName} onChange={e => setEditingName(e.target.value)} />
                                <button className="btn btn-xs btn-success mr-1" onClick={() => saveEdit(a.id)}><FiCheck /></button>
                                <button className="btn btn-xs btn-outline" onClick={() => setEditingId(null)}><FiX /></button>
                            </>
                        ) : (
                            <>
                                <span className="flex-1">{a.name}</span>
                                <button className="btn btn-xs btn-outline mr-1" onClick={() => startEdit(a)}><FiEdit2 /></button>
                                <button className="btn btn-xs btn-error" onClick={() => handleDelete(a.id)}><FiTrash2 /></button>
                            </>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default AssignmentManager;